/**
 * Issue #904 — refreshing partway through the new log-a-match flow
 * (`/performance`, behind `newUi("log-ground")`) used to drop a flag-on
 * player back on the shipped Performance Tracking list, with the flow in
 * progress simply gone.
 *
 * Root cause, both halves plain `useState` in `PerformanceTracking.jsx`:
 *
 *   - `open` (the dialog/flow visibility) came from `useCreateDialog`
 *     (`hooks/useCreateDialog.js`), which reads `?new=1` on the FIRST
 *     mount only and then strips it from the URL ("the param is
 *     consumed, not left in the URL" — that file's own header). By step
 *     2, the URL carries no `new=1` any more, so a refresh's fresh mount
 *     initialised `open` to `false`.
 *   - `logStep` (which of the flow's screens is showing — "ground",
 *     "add-ground", "form", "saved") had no URL tie at all, so even had
 *     `open` stayed true, a refresh would have reset it to "ground".
 *
 * Together: a refresh at step 2 landed on `open=false`, `logStep=
 * "ground"` — the shipped list, not even the new flow's own step 1.
 *
 * The fix restores the two DURABLE steps a fresh mount can genuinely
 * re-enter without depending on state that only exists in memory:
 *
 *   - "ground" — step 1, choosing where. Nothing this step needs came
 *     from a previous step.
 *   - "form" — step 2, the scorecard. Re-enterable even though the
 *     ground chosen in step 1 is lost on a hard refresh (page state, not
 *     saved anywhere) — `LogScorecardFlow` renders step 2 with a blank
 *     ground field exactly as it would for a player who tapped "Log
 *     match" and jumped straight to entering a scorecard nobody drafted
 *     yet. That is a real, working screen in the new flow, never the old
 *     list — the issue's own floor ("at least the new flow's start").
 *
 * "add-ground" (step 1's own sub-panel, mid-typing a new ground) and
 * "saved" (step 3, tied to the specific match id `create()` just wrote —
 * nothing here can refetch it from a URL alone) are deliberately NOT
 * restorable: a refresh during either falls back to "ground", the flow's
 * own start, per the same floor.
 */

/** The query param that asks the log flow to reopen at a specific step. */
export const LOG_STEP_PARAM = "step";

/** The only two `logStep` values a fresh mount can genuinely re-enter. */
export const RESTORABLE_LOG_STEPS = ["ground", "form"];

/**
 * The step a URL's search params ask to be restored to, or `null` when
 * the param is absent or names a step that isn't restorable (including a
 * stale/foreign value — an unrecognised `step` asks for nothing rather
 * than crashing the flow open on a step that doesn't exist).
 */
export function logStepFromParams(searchParams) {
  const v = searchParams.get(LOG_STEP_PARAM);
  return RESTORABLE_LOG_STEPS.includes(v) ? v : null;
}
